import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Activity, AlertTriangle, Database, ExternalLink, Pause, Play, RefreshCw, Server, Trash2 } from 'lucide-react'
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { Navigate } from 'react-router-dom'
import Layout from '../../components/Layout'
import { useAuth } from '../../contexts/AuthContext'
import { isAdminUser } from '../../helpers/accessControl'
import { schedulerService } from '../../services/schedulerService'

const dateTime = (value) => value ? new Date(value).toLocaleString('cs-CZ') : '—'
const number = (value) => Number(value || 0).toLocaleString('cs-CZ')
const waitDuration = (value) => {
  const seconds = Math.max(0, Math.ceil(Number(value || 0) / 1_000))
  if (seconds < 60) return `${seconds} s`
  const minutes = Math.ceil(seconds / 60)
  if (minutes < 60) return `${minutes} min`
  const hours = Math.floor(minutes / 60)
  const remainder = minutes % 60
  return remainder ? `${hours} h ${remainder} min` : `${hours} h`
}

const statusTones = {
  slate: { text: 'text-slate-700', background: 'bg-slate-50' },
  emerald: { text: 'text-emerald-700', background: 'bg-emerald-50' },
  red: { text: 'text-red-700', background: 'bg-red-50' },
  violet: { text: 'text-violet-700', background: 'bg-violet-50' },
  blue: { text: 'text-blue-700', background: 'bg-blue-50' },
  amber: { text: 'text-amber-700', background: 'bg-amber-50' },
}

const StatusCard = ({ title, value, note, tone = 'slate', Icon = Activity }) => {
  const colors = statusTones[tone] || statusTones.slate
  return (
  <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
    <div className="flex items-center justify-between gap-3">
      <div>
        <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{title}</div>
        <div className={`mt-2 text-2xl font-bold ${colors.text}`}>{value}</div>
      </div>
      <span className={`rounded-xl p-2.5 ${colors.background} ${colors.text}`}><Icon className="h-5 w-5" /></span>
    </div>
    {note && <div className="mt-2 text-xs text-slate-500">{note}</div>}
  </div>
  )
}

const listingScope = (job) => job?.data?.scopeKey || [job?.data?.region, job?.data?.offerType, job?.data?.family].filter(Boolean).join(' / ') || '—'

const SourcePageLink = ({ job, children }) => job?.data?.sourceUrl ? (
  <a href={job.data.sourceUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-inherit underline decoration-slate-300 underline-offset-2 hover:decoration-current">
    {children}<ExternalLink className="h-3 w-3 shrink-0" aria-hidden="true" />
  </a>
) : children

const PropertyLink = ({ propertyId }) => propertyId ? (
  <a href={`/detail/${encodeURIComponent(propertyId)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-inherit underline decoration-slate-300 underline-offset-2 hover:decoration-current">
    {propertyId}<ExternalLink className="h-3 w-3 shrink-0" aria-hidden="true" />
  </a>
) : '—'

const ListingDiscovery = ({ discovery, paused }) => {
  const active = discovery?.active
  const last = discovery?.lastCompleted
  const next = discovery?.next
  const counts = discovery?.counts || {}
  const page = (job) => job?.data?.page ? `str. ${job.data.page}` : '—'
  const estimate = next?.estimate
  const ahead = estimate?.aheadByType || {}
  const aheadBreakdown = [
    ahead.images && `obrázky ${number(ahead.images)}`,
    ahead.details && `detaily ${number(ahead.details)}`,
    ahead.listingPages && `přehledy ${number(ahead.listingPages)}`,
    ahead.other && `ostatní ${number(ahead.other)}`,
  ].filter(Boolean).join(' · ')
  const nextStatus = !next
    ? '—'
    : next.state === 'delayed'
      ? `Naplánováno ${dateTime(next.scheduledFor)}`
      : paused
        ? `Fronta je pozastavená · ${number(estimate?.jobsAhead)} úloh před ní`
        : estimate
          ? estimate.jobsAhead
            ? `Odhad startu za ~${waitDuration(estimate.durationMs)} · ${number(estimate.jobsAhead)} úloh před ní`
            : 'Je na řadě'
          : 'Čeká ve frontě'

  return (
    <div className="mt-4 rounded-2xl border border-blue-100 bg-blue-50/40 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Procházení přehledů</h3>
          <p className="mt-0.5 text-xs text-slate-500">Trvalý přehled listingových stran nezávisle na detailech a obrázcích.</p>
        </div>
        <div className="flex gap-2 text-xs tabular-nums text-slate-600">
          <span className="rounded-full bg-white px-2.5 py-1 ring-1 ring-blue-100">čeká {number(counts.waiting)}</span>
          <span className="rounded-full bg-white px-2.5 py-1 ring-1 ring-blue-100">odloženo {number(counts.delayed)}</span>
        </div>
      </div>
      <div className="mt-3 grid gap-3 lg:grid-cols-3">
        <div className={`rounded-xl border p-3 ${active ? 'border-blue-200 bg-white' : 'border-slate-200 bg-white/70'}`}>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-blue-700">Právě probíhá</div>
          <div className="mt-1.5 text-sm font-medium text-slate-900">{active ? <SourcePageLink job={active}>{`${listingScope(active)} · ${page(active)}`}</SourcePageLink> : 'Žádná listingová strana'}</div>
          <div className="mt-1 text-xs text-slate-500">{active ? `Od ${dateTime(active.processedOn)}` : 'Worker právě zpracovává obohacení nebo čeká.'}</div>
        </div>
        <div className="rounded-xl border border-emerald-100 bg-white p-3">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Poslední dokončená</div>
          <div className="mt-1.5 text-sm font-medium text-slate-900">{last ? <SourcePageLink job={last}>{`${listingScope(last)} · ${page(last)}`}</SourcePageLink> : 'Zatím bez dokončené strany'}</div>
          <div className="mt-1 text-xs text-slate-500">{last ? `${number(last.result?.listings)} nabídek · ${dateTime(last.finishedOn)}` : '—'}</div>
        </div>
        <div className="rounded-xl border border-violet-100 bg-white p-3">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-violet-700">Další v pořadí</div>
          <div className="mt-1.5 text-sm font-medium text-slate-900">{next ? <SourcePageLink job={next}>{`${listingScope(next)} · ${page(next)}`}</SourcePageLink> : 'Žádná další strana'}</div>
          <div className="mt-1 text-xs text-slate-500" title={estimate?.estimatedStartAt ? `Odhadovaný start ${dateTime(estimate.estimatedStartAt)}` : undefined}>{nextStatus}</div>
          {estimate && <div className="mt-1 text-[11px] text-slate-400">{aheadBreakdown || 'bez úloh před ní'} · tempo ~{waitDuration(estimate.typicalJobMs)} / úloha</div>}
        </div>
      </div>
    </div>
  )
}

const QueuePanel = ({ queue, lane, busyAction, onAction }) => {
  const counts = queue.counts || {}
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-950">{lane === 'priority' ? 'Prioritní fronta' : 'Národní fronta'}</h2>
          <p className="text-xs text-slate-500">{queue.name} · concurrency 1 · {queue.paused ? 'pozastavená' : 'aktivní'} · {lane === 'national' ? 'Praha → ostatní kraje' : 'přehledy → detaily → obrázky'}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" disabled={busyAction} onClick={() => onAction(queue.paused ? 'resume' : 'pause', lane)} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-xs font-medium hover:bg-slate-50 disabled:opacity-50">
            {queue.paused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}{queue.paused ? 'Spustit' : 'Pozastavit'}
          </button>
          <button type="button" disabled={busyAction} onClick={() => onAction('plan', lane)} className="rounded-lg bg-violet-700 px-3 py-2 text-xs font-medium text-white hover:bg-violet-800 disabled:opacity-50">Naplánovat teď</button>
          {lane === 'national' && <button type="button" disabled={busyAction} onClick={() => onAction('canary', lane)} className="rounded-lg border border-violet-200 px-3 py-2 text-xs font-medium text-violet-700 hover:bg-violet-50 disabled:opacity-50">Canary scope</button>}
          {lane === 'national' && <button type="button" disabled={busyAction} onClick={() => onAction('prague', lane)} className="rounded-lg border border-violet-200 px-3 py-2 text-xs font-medium text-violet-700 hover:bg-violet-50 disabled:opacity-50">Praha nyní</button>}
          <button type="button" disabled={busyAction} onClick={() => onAction('cancel', lane)} className="inline-flex items-center gap-2 rounded-lg border border-red-200 px-3 py-2 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"><Trash2 className="h-3.5 w-3.5" />Zrušit čekající</button>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-5">
        {['waiting', 'active', 'delayed', 'failed', 'completed'].map((state) => (
          <div key={state} className="rounded-xl bg-slate-50 p-3">
            <div className="text-xs text-slate-500">{state}</div>
            <div className="mt-1 text-xl font-semibold text-slate-900">{number(counts[state])}</div>
          </div>
        ))}
      </div>
      <ListingDiscovery discovery={queue.listingDiscovery} paused={queue.paused} />
      <div className="mt-4 overflow-x-auto">
        <table className="min-w-full text-left text-xs">
          <thead className="border-b text-slate-500"><tr><th className="py-2 pr-3">Stav / úloha</th><th className="py-2 pr-3">Scope</th><th className="py-2 pr-3">Strana / nemovitost</th><th className="py-2 pr-3">Chyba</th><th className="py-2">Akce</th></tr></thead>
          <tbody>
            {(queue.jobs || []).map((job) => (
              <tr key={job.id} className="border-b border-slate-100">
                <td className="py-2 pr-3"><div className="font-medium">{job.name}</div><div className="text-slate-400">{job.state || '—'} · {job.id?.slice(0, 10)}</div></td>
                <td className="py-2 pr-3"><SourcePageLink job={job}>{job.data?.scopeKey || job.data?.region || '—'}</SourcePageLink></td>
                <td className="py-2 pr-3">{job.data?.page ? <SourcePageLink job={job}>{`str. ${job.data.page}`}</SourcePageLink> : <PropertyLink propertyId={job.data?.propertyId} />}</td>
                <td className="max-w-xs truncate py-2 pr-3 text-red-700">{job.failedReason || '—'}</td>
                <td className="py-2">{job.state === 'failed' && <button type="button" onClick={() => onAction('retry', lane, job.id)} className="font-medium text-violet-700 hover:underline">Zkusit znovu</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!queue.jobs?.length && <div className="py-6 text-center text-sm text-slate-400">Fronta je prázdná.</div>}
      </div>
      <div className="mt-4 border-t border-slate-100 pt-3 text-xs text-slate-500">
        {(queue.schedulers || []).map((scheduler) => <div key={scheduler.id}>{scheduler.name}: příště {dateTime(scheduler.next)}</div>)}
      </div>
    </section>
  )
}

const Scheduler = () => {
  const { user } = useAuth()
  const admin = isAdminUser(user)
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  const [busyAction, setBusyAction] = useState(false)

  const load = useCallback(async () => {
    if (!admin) return
    try {
      const result = await schedulerService.status()
      setData(result)
      setError('')
    } catch (requestError) {
      setError(requestError.message || 'Stav scheduleru se nepodařilo načíst.')
    } finally {
      setLoading(false)
    }
  }, [admin])

  useEffect(() => {
    if (!admin) return undefined
    load()
    const timer = window.setInterval(load, 15_000)
    return () => window.clearInterval(timer)
  }, [admin, load])

  const handleAction = async (action, lane, jobId) => {
    if (action === 'cancel' && !window.confirm(`Opravdu zrušit všechny čekající úlohy ve frontě ${lane}?`)) return
    setBusyAction(true)
    try {
      if (action === 'pause') await schedulerService.pause(lane)
      if (action === 'resume') await schedulerService.resume(lane)
      if (action === 'plan') await schedulerService.plan(lane, 'full')
      if (action === 'canary') await schedulerService.plan(lane, 'canary')
      if (action === 'prague') await schedulerService.plan(lane, 'prague')
      if (action === 'cancel') await schedulerService.cancelPending(lane)
      if (action === 'retry') await schedulerService.retryJob(lane, jobId)
      if (action === 'reconciliation') await schedulerService.setReconciliation(!data?.controls?.reconciliationEnabled)
      await load()
    } catch (requestError) {
      setError(requestError.message || 'Akci se nepodařilo provést.')
    } finally {
      setBusyAction(false)
    }
  }

  const chartData = useMemo(() => (data?.metrics || []).map((metric) => ({
    hour: new Date(metric.hour).toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' }),
    requests: metric.requests || 0,
    errors: (metric.statuses?.error || 0) + (metric.errors ? Object.values(metric.errors).reduce((sum, value) => sum + value, 0) : 0),
  })), [data])

  const workerAlive = data?.worker?.at && Date.now() - new Date(data.worker.at).getTime() < 90_000
  const queues = Object.fromEntries((data?.queues || []).map((queue) => [queue.name.includes('priority') ? 'priority' : 'national', queue]))
  const prague = data?.pragueProgress
  const pragueRuns = (data?.scopeMatrix || []).filter((run) => run.scopeKey?.startsWith('national/praha/'))
  const pragueSuccessful = pragueRuns.filter((run) => run.status === 'successful').length
  const header = <div className="flex items-center gap-3"><Activity className="h-5 w-5 text-violet-700" /><h1 className="text-xl font-semibold">Scheduler scraperu</h1></div>

  if (!admin) return <Navigate to="/dashboard" replace />

  return (
    <Layout headerContent={header}>
      <div className="w-full space-y-6 p-4 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div><h1 className="text-2xl font-bold text-slate-950">SReality provoz</h1><p className="mt-1 text-sm text-slate-500">Živá fronta v Redis, trvalá historie v MongoDB.</p></div>
          <div className="flex gap-2">
            <button type="button" disabled={busyAction} onClick={() => handleAction('reconciliation', 'all')} className={`rounded-lg border px-3 py-2 text-xs font-medium ${data?.controls?.reconciliationEnabled ? 'border-amber-300 bg-amber-50 text-amber-800' : 'border-slate-300 bg-white text-slate-700'}`}>Inaktivita: {data?.controls?.reconciliationEnabled ? 'zapnuta' : 'vypnuta'}</button>
            <button type="button" disabled={busyAction} onClick={() => handleAction(data?.controls?.pauseAll ? 'resume' : 'pause', 'all')} className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white ${data?.controls?.pauseAll ? 'bg-emerald-700' : 'bg-red-700'}`}>{data?.controls?.pauseAll ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}{data?.controls?.pauseAll ? 'Spustit vše' : 'Nouzově zastavit vše'}</button>
            <button type="button" onClick={load} className="rounded-lg border border-slate-300 p-2 text-slate-700 hover:bg-white"><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /></button>
          </div>
        </div>

        {error && <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">{error}</div>}
        {loading && !data ? <div className="py-24 text-center text-slate-500">Načítám scheduler…</div> : data && <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatusCard title="Worker" value={workerAlive ? 'Online' : 'Offline'} note={data.worker ? `Heartbeat ${dateTime(data.worker.at)}` : 'Heartbeat nebyl nalezen'} tone={workerAlive ? 'emerald' : 'red'} Icon={Server} />
            <StatusCard title="Redis" value={data.redis?.ping || '—'} note={`${data.redis?.usedMemory || '—'} · ${data.redis?.policy || '—'}`} tone="violet" Icon={Database} />
            <StatusCard title="Detail requests" value={`${number(data.budgets?.detail?.used)} / ${number(data.budgets?.detail?.limit)}`} note={data.budgets?.date} tone="blue" Icon={Activity} />
            <StatusCard title="Image requests" value={`${number(data.budgets?.image?.used)} / ${number(data.budgets?.image?.limit)}`} note={`Listing pages ${number(data.budgets?.listing?.used)} / ${number(data.budgets?.listing?.limit)}`} tone="amber" Icon={AlertTriangle} />
          </div>

          {data.controls?.circuitUntil && <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">SReality circuit breaker je otevřený do {dateTime(data.controls.circuitUntil)}.</div>}

          {prague && <section className="rounded-2xl border border-violet-200 bg-violet-50/40 p-5 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div><h2 className="text-lg font-semibold text-slate-950">Praha · doplnění dat</h2><p className="mt-1 text-xs text-slate-600">V národní frontě, bez upozornění. Stejné limity požadavků a ochrana Redis jako pro ostatní kraje.</p></div>
              <span className="rounded-full bg-white px-3 py-1 text-xs text-violet-800 ring-1 ring-violet-200">{number(pragueSuccessful)} / 20 scope dokončeno</span>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-xl bg-white p-3"><div className="text-xs text-slate-500">Aktivní nemovitosti</div><div className="mt-1 text-xl font-semibold">{number(prague.activeProperties)}</div></div>
              <div className="rounded-xl bg-white p-3"><div className="text-xs text-slate-500">Detaily doplněny</div><div className="mt-1 text-xl font-semibold">{number(prague.detailsCaughtUp)} / {number(prague.activeProperties)}</div><div className="text-xs text-slate-500">čeká na detail {number(prague.detailsPending)}</div></div>
              <div className="rounded-xl bg-white p-3"><div className="text-xs text-slate-500">Uložené obrázky</div><div className="mt-1 text-xl font-semibold">{number(prague.mirroredImages)} / {number(prague.sourceImages)}</div><div className="text-xs text-slate-500">známé zdrojové obrázky</div></div>
              <div className="rounded-xl bg-white p-3"><div className="text-xs text-slate-500">Nedostupné obrázky</div><div className="mt-1 text-xl font-semibold">{number(prague.terminalImageFailures)}</div><div className="text-xs text-slate-500">po omezených pokusech</div></div>
            </div>
            <p className="mt-3 text-xs text-slate-500">Stav {dateTime(prague.measuredAt)} · počet zdrojových obrázků roste s doplněnými detaily.</p>
          </section>}

          <div className="grid gap-6 xl:grid-cols-2">
            {queues.priority && <QueuePanel queue={queues.priority} lane="priority" busyAction={busyAction} onAction={handleAction} />}
            {queues.national && <QueuePanel queue={queues.national} lane="national" busyAction={busyAction} onAction={handleAction} />}
          </div>

          <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="text-lg font-semibold">Požadavky za posledních 24 hodin</h2>
            <div className="mt-4 h-64">
              <ResponsiveContainer width="100%" height="100%"><LineChart data={chartData}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="hour" /><YAxis allowDecimals={false} /><Tooltip /><Line type="monotone" dataKey="requests" stroke="#6d28d9" strokeWidth={2} /><Line type="monotone" dataKey="errors" stroke="#dc2626" strokeWidth={2} /></LineChart></ResponsiveContainer>
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="text-lg font-semibold">Poslední stav jednotlivých scope</h2>
            <div className="mt-4 max-h-[560px] overflow-auto">
              <table className="min-w-full text-left text-xs"><thead className="sticky top-0 bg-white text-slate-500"><tr><th className="py-2 pr-3">Scope</th><th className="py-2 pr-3">Stav</th><th className="py-2 pr-3">Strany</th><th className="py-2 pr-3">Nabídky</th><th className="py-2 pr-3">Dokončeno</th><th className="py-2">Chyba</th></tr></thead><tbody>{(data.scopeMatrix || []).map((run) => <tr key={run.scopeKey} className="border-t border-slate-100"><td className="py-2 pr-3 font-medium">{run.scopeKey}</td><td className="py-2 pr-3">{run.status}</td><td className="py-2 pr-3">{number(run.pagesCompleted)} / {number(run.expectedPages)}</td><td className="py-2 pr-3">{number(run.ownedListingsSeen)}</td><td className="py-2 pr-3">{dateTime(run.completedAt)}</td><td className="max-w-sm truncate py-2 text-red-700">{run.failure?.message || run.deferredReason?.message || '—'}</td></tr>)}</tbody></table>
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="text-lg font-semibold">Provozní události</h2>
            <div className="mt-3 divide-y divide-slate-100">{(data.events || []).slice(0, 30).map((event, index) => <div key={`${event.at}-${index}`} className="flex flex-wrap justify-between gap-2 py-2 text-xs"><span className="font-medium">{event.type} {event.lane ? `· ${event.lane}` : ''}</span><span className="text-slate-500">{dateTime(event.at)}</span></div>)}</div>
          </section>
        </>}
      </div>
    </Layout>
  )
}

export default Scheduler
